import { Modify } from '@shared/models/common.model';
import { CertificateStatus } from '../../certificate/shared/certificate.model';
import {
  DdocPerson,
  DepartmentDisplay,
  PersonNameDetail,
} from '@shared/models/user.model';

export interface DDocSigningStatus {
  ddoc_certified_mode: boolean;
  ddoc_locked_mode: boolean;
  ddoc_use_26: boolean;
  ddoc_use_28: boolean;
}

export interface Loa {
  count: number;
  delegate_value: boolean[];
  department_name_en: string[];
  department_name: string[];
  departments: number[];
  group_type: number;
  id: number;
  is_custom: boolean;
  levels: Level[];
  max_level: number;
  memo_types: string;
  name: string;
}

export interface LoaListItem {
  id: number;
  department_display: DepartmentDisplay[];
  is_all_department: boolean;
  is_allow_edit: boolean;
  is_from_template: boolean;
  name: string;
  name_en: string;
  max_level: number;
  original_loa: unknown | null; // TODO: Update unknown type
  site: number;
  department: number[];
  created_at: string;
  created_user: PersonNameDetail;
}

export interface LoaResponse {
  count: number;
  next: string;
  previous?: any;
  results: LoaListItem[];
}

export interface Member {
  id: number;
  is_delegate: boolean;
  delegate_type: 'normal' | 'e_poa';
  job_position_name: string;
  level: number;
  person: any;
  person_detail?: DdocPerson | null;
  m28_signature_preview?: string;
  full_name?: string;
  sequence?: number;
  signature?: string;
  no_person: boolean;
  ddoc_use_26?: boolean;
  ddoc_use_28?: boolean;
  ddoc_certified_mode: boolean;
  ddoc_locked_mode: boolean;
  m28_certificate_status?: CertificateStatus;
  m26_certificate_status?: CertificateStatus;
  is_need_e_kyc: boolean;
  e_kyc_type: EKYCType | null;
  e_kyc_frequency: EKYCFrequency | null;
  is_allow_change_e_kyc_type: boolean;
  evidence?: any;
  job_position?: string;
  verification?: Verification;
  invalid?: boolean;
  invalidFields?: {
    email?: boolean;
    name?: boolean;
    job_position?: boolean;
    phone_number?: boolean;
  };
}

export interface Level<LoaMember = Member> {
  id: number;
  members: LoaMember[];
  count: number;
  level: number;
  min_approve_count: number;
  name: string;
  signature_required: boolean;
  site: number;
  group: number;
  user_type?: LOALevelUserType;
  ddoc_enable?: boolean;
  loa_level_detail?: any;
}

export interface LoaModel {
  id: number;
  levels: Level<Signer>[];
  name: string;
  is_custom: boolean;
  memo_type?: any;
  max_level: number;
  site: number;
  department: number[];
  count?: number;
  name_en: string;
  is_allow_edit?: boolean;
  is_remove_lower: boolean;
}

export interface LoaDetail extends Omit<LoaModel, 'memo_type'> {
  context: { name_en: string };
  is_all_department: boolean;
  is_from_template: boolean;
  is_cloned_loa: boolean;
  label: string;
  has_dynamic_loa: boolean;
  init_dynamic_loa: boolean;
  budget_loa_group?: BudgetLoaGroup;
  original_loa?: number;
}

export interface BudgetLoaGroup {
  categories: number[];
  is_over_budget: boolean;
  is_unlimited: boolean;
  min_amount: number | null;
  max_amount: number | null;
}

export type LoaDetailPatch = Modify<
  Pick<LoaDetail, 'levels' | 'is_custom'>,
  { id?: number }
>;

export interface PeopleList {
  people: People[];
  _count: Count;
}

export interface PassEKYC {
  ever: boolean;
  in_1_month: boolean;
  in_6_month: boolean;
}

export interface NonUser {
  id?: number;
  name?: string | null;
  first_name: string | null;
  last_name: string | null;
  job_position: string | null;
  email: string | null;
  is_custom: boolean;
  phone_number?: string | null;
  pass_ndid: PassEKYC;
  pass_thaicom: PassEKYC;
}

export interface People {
  label: string;
  value: number;
  context: Context;
}

export interface Context {
  email: string;
}

export interface Count {
  people: number;
}

export type Signer = Member & Partial<DDocSigningStatus>;

export enum LoaUserTypeEnum {
  User = 'user',
  NoneUser = 'none user',
  Creator = 'creator',
  JobPosition = 'job position',
  Loh = 'level of hierarchy',
}

export type LOALevelUserType =
  | LoaUserTypeEnum.User
  | LoaUserTypeEnum.NoneUser
  | LoaUserTypeEnum.Creator
  | LoaUserTypeEnum.JobPosition
  | LoaUserTypeEnum.Loh;

export type EKYCType = 'ndid' | 'thaicom' | 'not specific';
export type EKYCFrequency =
  | 'first time only'
  | 'always'
  | 'every 1 month'
  | 'every 6 month';

export const INITIAL_NON_USER_PERSON_DATA: NonUser = {
  is_custom: true,
  first_name: null,
  last_name: null,
  email: null,
  job_position: null,
  phone_number: null,
  pass_ndid: {
    ever: false,
    in_1_month: false,
    in_6_month: false,
  },
  pass_thaicom: {
    ever: false,
    in_1_month: false,
    in_6_month: false,
  },
};

export class Verification {
  id?: number;
  memo?: number;
  loa_member?: number;

  constructor(
    public otp_required = false,
    public is_from_template = false,
    public is_attestor = false,
    public phone_number: string | null = null,
    public review_evidence_status: string | null = null,
    public resubmit_reason: string | null = null,
    public start_evidence_request_at: string | null = null,
    public reminded = false,
    public hard_reminded = false,
    public selfie_video_required = false,
    public selfie_video_script: string | null = null,
    public selfie_video_file: string | null = null,
    public selfie_video_file_name: string | null = null,
    public selfie_video_uploaded_at: string | null = null,
    public selfie_video_is_resubmitting = false,
    public has_request_attachments = false,
    public pass_request_attachments = false,
    public loa_member_type: string | null = null,
    public review_evidence_attachments_status: string | null = null,
    public review_evidence_selfie_video_status: string | null = null,
  ) {}
}
